<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import DialogWrapper from '@/wrappers/DialogWrapper.vue';
  import { useContactModal } from './contactModal.composable';
  import CustomPhoneField from '@/components/inputs/form/CustomPhoneField.vue';
  import CustomEmailField from '@/components/inputs/form/CustomEmailField.vue';
  import identity from '@/utils/identity.utility';
  import WarningUSPhone from '@/components/inputs/form/WarningUSPhone.vue';
  import { Country } from '@/models/types';

  const emit = defineEmits<{
    (e: 'loading', isLoading: boolean): void;
  }>();

  const { getDestiName } = identity();

  const {
    contact,
    customTelRef,
    isCustomTelRefRequired,
    customEmailRef,
    isCustomEmailRefRequired,
    isContactEmpty,
    updateRequired,
    handleClose,
    save,
  } = useContactModal({
    close,
    emit,
  });

  const dialog = ref<typeof DialogWrapper>();
  const selectedCodePhone = ref<Country>();

  function showModal() {
    dialog.value?.showModal();
  }

  function close() {
    dialog.value?.close();
  }

  onMounted(() => {
    updateRequired();
  });

  defineExpose({ showModal, close });
</script>

<template>
  <dialog-wrapper
    @close="handleClose"
    ref="dialog"
    class="send-roadbook-dialog"
  >
    <header>
      <p class="font-semibold text-2xl text-center text-primary mb-4 mx-4">
        {{ $t('client_info.coordinates') }}
      </p>
    </header>

    <form
      method="dialog"
      class="flex flex-wrap gap-2 mb-4 max-w-[1000px]"
      @submit.prevent="save"
    >
      <label
        :for="`tel`"
        class="sr-only"
      >
        Téléphone (Format E.164) :
      </label>
      <custom-phone-field
        ref="customTelRef"
        :index="`tel`"
        v-model:value="contact.phone"
        v-model:required="isCustomTelRefRequired"
        @input="updateRequired"
        @selected-phone-code="(newCode) => (selectedCodePhone = newCode)"
      />

      <label
        :for="`custom-search-select-input-email`"
        class="sr-only"
      >
        Courriel :
      </label>
      <custom-email-field
        ref="customEmailRef"
        :index="`email`"
        v-model:value="contact.email"
        v-model:required="isCustomEmailRefRequired"
        @input="updateRequired"
        class="min-w-[500px]"
      />

      <label
        :for="`firstname`"
        class="sr-only"
      >
        Prénom :
      </label>
      <input
        type="text"
        :id="`firstname`"
        :name="`firstname`"
        v-model="contact.first_name"
        :placeholder="$t('client_info.firstname')"
        class="bg-grey-lightest p-4 py-2 rounded-md mb-2 grow"
      />
      <label
        :for="`lastname`"
        class="sr-only"
      >
        Nom :
      </label>
      <input
        type="text"
        :id="`lastname`"
        :name="`lastname`"
        v-model="contact.last_name"
        :placeholder="$t('client_info.lastname')"
        class="bg-grey-lightest p-4 py-2 rounded-md mb-2 grow"
      />

      <label
        :for="`opt-in`"
        class="h-10 rounded-md self-start p-2 cursor-pointer hover:bg-ternary focus-within:bg-ternary flex items-center"
      >
        <input
          type="checkbox"
          :id="`opt-in`"
          :name="`opt-in`"
          v-model="contact.is_optin"
        />
        <small class="ml-2">
          {{ $t('client_info.newsletter_optin') }} {{ $t('from') }} {{ getDestiName() }}</small
        ></label
      >
      <warning-u-s-phone
        :phone="contact.phone"
        :selected-code-phone="selectedCodePhone"
      />
      <div class="flex w-full justify-center gap-4">
        <button
          type="submit"
          class="bg-primary text-white font-semibold px-8 py-2.5 rounded-xl hover:bg-secondary hover:text-white flex gap-2 items-center flex-nowrap justify-center w-fit"
        >
          {{ isContactEmpty ? $t('client_info.save_without_coordinates') : $t('btn.save') }}
        </button>
      </div>
    </form>
  </dialog-wrapper>
</template>

<style scoped>
  input:invalid {
    border: 2px solid var(--red-error);
    border-left: 6px solid var(--red-error);
  }
  input:placeholder-shown {
    border: none;
  }
  input:focus-within {
    border: none;
  }
  input[required] {
    border-left: 6px solid var(--red-error);
  }
  input:valid {
    border-left: 6px solid var(--green-valid);
  }

  /* This is not possible in version three of Tailwind. */
  label:has(input[type='checkbox']:checked) {
    border-color: var(--primary);
    background-color: var(--ternary);
  }
</style>
